import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { PostgrestError, SupabaseClient, User } from '@supabase/supabase-js';
import { ADMIN_ROUTE_PREFIX, AGENT_ROUTE_PREFIX } from '@/constants/security';
import { supabase } from '@/integrations/supabase/client';
import {
  AccessContextQueryError,
  type GetMyActorAccessContextRpcRow,
  type MyAccessContext,
} from '@/types/profiles';

export const accessContextKeys = {
  all: ['access-context'] as const,
  detail: () => [...accessContextKeys.all, 'detail'] as const,
};

export interface UseMyAccessContextOptions {
  enabled?: boolean;
  client?: SupabaseClient;
}

export interface UseMyAccessContextResult {
  data: MyAccessContext | null;
  accessContext: MyAccessContext | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: AccessContextQueryError | null;
  refetch: UseQueryResult<MyAccessContext, AccessContextQueryError>['refetch'];
  invalidate: () => Promise<void>;
}

/**
 * Normaliza errores provenientes de Supabase/PostgREST a un dominio de errores
 * explícito para la UI de acceso/autenticación.
 */
function normalizePostgrestError(error: PostgrestError): AccessContextQueryError {
  if (error.code === '28000' || error.message === 'not_authenticated') {
    return new AccessContextQueryError({
      kind: 'not_authenticated',
      message: 'El usuario no está autenticado.',
      code: error.code,
      details: error.details,
      hint: error.hint,
      cause: error,
    });
  }

  if (
    error.code === 'P0001' &&
    (error.message === 'profile_not_found' || error.message === 'actor_access_not_found')
  ) {
    return new AccessContextQueryError({
      kind: 'profile_not_found',
      message: 'No se encontró un registro de acceso para el usuario autenticado.',
      code: error.code,
      details: error.details,
      hint: error.hint,
      cause: error,
    });
  }

  if (error.code === '42501') {
    return new AccessContextQueryError({
      kind: 'permission_denied',
      message: 'No tienes permisos para consultar el contexto de acceso.',
      code: error.code,
      details: error.details,
      hint: error.hint,
      cause: error,
    });
  }

  return new AccessContextQueryError({
    kind: 'network_or_unknown',
    message: error.message || 'Ocurrió un error inesperado al consultar el contexto de acceso.',
    code: error.code,
    details: error.details,
    hint: error.hint,
    cause: error,
  });
}

/**
 * Convierte errores desconocidos a un tipo uniforme del dominio de acceso.
 */
function normalizeUnknownError(error: unknown): AccessContextQueryError {
  if (error instanceof AccessContextQueryError) {
    return error;
  }

  return new AccessContextQueryError({
    kind: 'network_or_unknown',
    message: 'Ocurrió un error inesperado al consultar el contexto de acceso.',
    cause: error,
  });
}

function readUserMetadataString(user: User | null, ...keys: string[]): string | null {
  const metadata = user?.user_metadata;

  if (!metadata || typeof metadata !== 'object') {
    return null;
  }

  for (const key of keys) {
    const value = Reflect.get(metadata, key);
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }

  return null;
}

function resolveDashboardPath(roleCode: GetMyActorAccessContextRpcRow['role_code']): string | null {
  switch (roleCode) {
    case 'super_admin':
    case 'admin':
      return ADMIN_ROUTE_PREFIX;
    case 'agent':
      return AGENT_ROUTE_PREFIX;
    default:
      return null;
  }
}

/**
 * Mapea el row canonico de actor_access v2 a un view model estable para frontend.
 */
function mapAccessContextRow(
  row: GetMyActorAccessContextRpcRow,
  user: User | null,
): MyAccessContext {
  const isSuperAdmin = row.role_code === 'super_admin';
  const isAdminOrSuperAdmin = row.is_admin_or_super_admin;

  return {
    userId: row.actor_id,
    email: user?.email ?? '',
    displayName: readUserMetadataString(user, 'display_name', 'full_name', 'name'),
    avatarUrl: readUserMetadataString(user, 'avatar_url', 'picture'),
    phone: readUserMetadataString(user, 'phone'),
    roleCode: row.role_code,
    accessStatus: row.access_status,
    dashboardPath: resolveDashboardPath(row.role_code),
    canAccess: row.can_access,
    isInternalOperator: row.is_internal_operator,
    isAdmin: isAdminOrSuperAdmin,
    isAdminOrSuperAdmin,
    isSuperAdmin,
    isAgent: row.role_code === 'agent',
    isOwner: row.role_code === 'owner',
    isTenant: row.role_code === 'tenant',
  };
}

/**
 * Ejecuta la RPC get_my_actor_access_context_v2 y devuelve un único contexto de acceso
 * ya normalizado para la UI.
 *
 * Caching recomendado:
 * - staleTime: 60s
 * - gcTime: 30m
 *
 * Esta query NO usa optimistic updates y NO requiere paginación.
 */
export async function fetchMyAccessContext(
  client: SupabaseClient,
): Promise<MyAccessContext> {
  try {
    const [{ data, error }, sessionResult] = await Promise.all([
      client.rpc('get_my_actor_access_context_v2' as never),
      client.auth.getSession(),
    ]);

    if (error) {
      throw normalizePostgrestError(error as PostgrestError);
    }

    const rows = (data ?? null) as GetMyActorAccessContextRpcRow[] | null;
    const row = rows?.[0];

    if (!row) {
      throw new AccessContextQueryError({
        kind: 'unexpected_empty_result',
        message: 'La RPC get_my_actor_access_context_v2 no devolvió registros.',
      });
    }

    const sessionUser = sessionResult.data.session?.user ?? null;
    const user = sessionUser
      ? sessionUser
      : (await client.auth.getUser()).data.user ?? null;

    return mapAccessContextRow(row, user);
  } catch (error: unknown) {
    throw normalizeUnknownError(error);
  }
}

/**
 * Hook de lectura del contexto de acceso del usuario autenticado.
 *
 * Uso recomendado:
 * - guards de ruta
 * - layout bootstrap
 * - navegación por rol
 * - banners o flujos de MFA
 *
 * @param options.enabled Permite desactivar la ejecución condicionalmente.
 * @param options.client Cliente Supabase opcional para facilitar testing o DI.
 *
 * @returns
 * - data/accessContext: contexto de acceso actual
 * - estados de carga y error
 * - refetch manual
 * - invalidate precisa para este recurso
 */
export function useMyAccessContext(
  options: UseMyAccessContextOptions = {},
): UseMyAccessContextResult {
  const queryClient = useQueryClient();
  const client = options.client ?? supabase;
  const enabled = options.enabled ?? true;

  const query = useQuery<MyAccessContext, AccessContextQueryError>({
    queryKey: accessContextKeys.detail(),
    queryFn: () => fetchMyAccessContext(client),
    enabled,
    staleTime: 60_000,
    gcTime: 30 * 60_000,
    retry: (failureCount, error) => {
      if (
        error.kind === 'not_authenticated' ||
        error.kind === 'profile_not_found' ||
        error.kind === 'permission_denied' ||
        error.kind === 'unexpected_empty_result'
      ) {
        return false;
      }

      return failureCount < 2;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    data: query.data ?? null,
    accessContext: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error ?? null,
    refetch: query.refetch,
    invalidate: async () => {
      await queryClient.invalidateQueries({
        queryKey: accessContextKeys.detail(),
      });
    },
  };
}